import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../../components/tooltip/tooltip';
import InfoIcon from '../../../img/icons/info.svg';
import styles from './categorySelect.module.css';

export default function CategorySelect ({ categories, setFormData }) {
  const navigate = useNavigate();
  const [isValidLocal, setIsValidLocal] = useState(false);
  const [localCategories, setLocalCategories] = useState(categories);
  const searchParams = new URLSearchParams(window.location.search);
  const [newUrlParam, setNewUrlParam] = useState(
    searchParams.get('s') ? searchParams.get('s').split(',') : []
  );

  const addUniqueValue = (val) => {
    // If contains A exclude all prev values for that category
    if (val[1] === 'A' && newUrlParam.length > 0) {
      const filteredList = newUrlParam.filter((str) => !str.startsWith(val[0]));
      setNewUrlParam([...filteredList, val]);
    } else if (!newUrlParam.includes(val)) {
      setNewUrlParam([...newUrlParam, val]);
    }
  };

  const removeValue = (val) => {
    let tempParam = newUrlParam;
    if (val[1] !== 'A' && parseInt(val[0]) != 5) {
      // If no longer selecting whole category
      if (
        localCategories[parseInt(val[0])].selectSubCategories ==
        localCategories[parseInt(val[0])].subCategories.length - 1
      ) {
        // Add all other subcategories that are still selected to urlparam
        for (
          let i = 0;
          i < localCategories[parseInt(val[0])].subCategories.length;
          i++
        ) {
          if (i !== parseInt(val[1])) {
            tempParam = [...tempParam, `${val[0]}${i}`];
          }
        }
        // Remove unselected subcategory from param
        const filteredList = tempParam.filter((str) => str !== `${val[0]}A`);
        setNewUrlParam(filteredList);
      } else {
        const filteredList = newUrlParam.filter((str) => str !== val);
        setNewUrlParam(filteredList);
      }
    } else {
      // Remove unselected category from param
      const filteredList = newUrlParam.filter((str) => str[0] !== val[0]);
      setNewUrlParam(filteredList);
    }
  };

  useEffect(() => {
    // Update url params
    if (newUrlParam.length > 0) {
      searchParams.set('s', newUrlParam);
    } else {
      searchParams.delete('s');
    }
    navigate(`?${searchParams.toString()}`, { replace: true });
  }, [newUrlParam]);

  const validateForm = () => {
    setIsValidLocal(false);
    localCategories.forEach((category) => {
      if (category.selectSubCategories > 0) {
        setIsValidLocal(true);
      }
    });
  };

  const handleChange = (index, subIndex = null) => {
    const tempCategories = categories;

    if (subIndex != null) {
      // Subcategory selected
      tempCategories[index].subCategories[subIndex].isSelected =
        !tempCategories[index].subCategories[subIndex].isSelected;

      if (tempCategories[index].isSelectAllOnly == true) {
        if (tempCategories[index].subCategories[subIndex].isSelected == true) {
          tempCategories[index].selectSubCategories =
            tempCategories[index].subCategories.length;
          addUniqueValue(`${index}A`);
        } else {
          tempCategories[index].selectSubCategories = 0;
        }
      } else {
        if (tempCategories[index].subCategories[subIndex].isSelected == true) {
          tempCategories[index].selectSubCategories += 1;
          addUniqueValue(`${index}${subIndex}`);
        } else {
          tempCategories[index].selectSubCategories -= 1;
          removeValue(`${index}${subIndex}`);
        }
      }
    } else {
      // Category selected
      tempCategories[index].isSelected = !tempCategories[index].isSelected;

      if (tempCategories[index].isSelected == true) {
        tempCategories[index].selectSubCategories =
          tempCategories[index].subCategories.length;
      } else {
        tempCategories[index].selectSubCategories = 0;
        removeValue(`${index}A`);
      }
    }

    // Select all logic
    if (
      tempCategories[index].selectSubCategories ==
      tempCategories[index].subCategories.length
    ) {
      tempCategories[index].isSelected = true;
      tempCategories[index].subCategories.forEach((item) => {
        item.isSelected = true;
      });

      addUniqueValue(`${index}A`);
    } else if (tempCategories[index].selectSubCategories == 0) {
      tempCategories[index].isSelected = false;
      tempCategories[index].subCategories.forEach((item) => {
        item.isSelected = false;
      });
      removeValue(`${index}A`);
    } else {
      tempCategories[index].isSelected = false;
    }

    // Validation
    setLocalCategories(tempCategories);
    validateForm();
  };

  useEffect(() => {
    // Update form values after validation
    setFormData((prevData) => ({
      ...prevData,
      servicesStep: {
        serviceCategories: localCategories,
        isValid: isValidLocal
      }
    }));
  }, [isValidLocal]);

  // Page load
  useEffect(() => {
    validateForm();
  }, []);

  const tooltipContent = [
    <div id='tooltip-1' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 1 - Home and community services
      </h6>
      <p className={styles.toolTipContentText} style={{ marginTop: '17px' }}>
        Services under Category 1 include:
      </p>
      <p className={styles.toolTipContentSubheading}>Domestic Assistance</p>
      <ul id='Cat-1-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>General house cleaning</li>
        <li className={styles.toolTipContentText}>Laundry services</li>
        <li className={styles.toolTipContentText}>Shopping assistance</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Home Maintenance and Repairs
      </p>
      <ul id='Cat-1-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Gardening</li>
        <li className={styles.toolTipContentText}>
          Assistance with home maintenance and repairs
        </li>
        <li className={styles.toolTipContentText}>
          Expenses for home maintenance and repairs
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Meals</p>
      <ul id='Cat-1-list-3' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Meal delivery</li>
        <li className={styles.toolTipContentText}>Meal preparation</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Transport</p>
      <ul id='Cat-1-list-4' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Direct transport</li>
        <li className={styles.toolTipContentText}>Indirect transport</li>
      </ul>
    </div>,
    <div id='tooltip-2' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 2 - Assistive technology and home modifications
      </h6>
      <p className={styles.toolTipContentText}>
        Services under Category 2 include:
      </p>
      <p className={styles.toolTipContentSubheading}>Equipment and Products</p>
      <ul id='Cat-2-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Managing body functions</li>
        <li className={styles.toolTipContentText}>Self-care products</li>
        <li className={styles.toolTipContentText}>Mobility products</li>
        <li className={styles.toolTipContentText}>Domestic life products</li>
        <li className={styles.toolTipContentText}>
          Communication and information management products
        </li>
        <li className={styles.toolTipContentText}>
          Assistive technology prescription and clinical support
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Home Adjustments</p>
      <ul id='Cat-2-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Home modification products
        </li>
        <li className={styles.toolTipContentText}>
          Home modifications prescription and clinical support
        </li>
      </ul>
    </div>,
    <div id='tooltip-3' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 3 - Advisory and support services
      </h6>
      <p className={styles.toolTipContentText}>
        Services under Category 3 include:
      </p>
      <p className={styles.toolTipContentSubheading}>
        Hoarding and Squalor Assistance
      </p>
      <ul id='Cat-3-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Hoarding and squalor supports
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Social Support and Community Engagement
      </p>
      <ul id='Cat-3-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Group social support</li>
        <li className={styles.toolTipContentText}>Individual social support</li>
        <li className={styles.toolTipContentText}>Accompanied activities</li>
        <li className={styles.toolTipContentText}>Cultural support</li>
        <li className={styles.toolTipContentText}>
          Digital education and support
        </li>
        <li className={styles.toolTipContentText}>
          Assistance to maintain personal affairs
        </li>
        <li className={styles.toolTipContentText}>
          Expenses to maintain personal affairs
        </li>
      </ul>
    </div>,
    <div id='tooltip-4' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 4 - Personal care and care support in the home or community
        (including respite)
      </h6>
      <p className={styles.toolTipContentText}>
        Services under Category 4 include:
      </p>
      <p className={styles.toolTipContentSubheading}>
        Allied Health and Therapy
      </p>
      <ul id='Cat-4-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Allied health assistance</li>
        <li className={styles.toolTipContentText}>Podiatry</li>
        <li className={styles.toolTipContentText}>Social work</li>
        <li className={styles.toolTipContentText}>Speech Pathology</li>
        <li className={styles.toolTipContentText}>Diet or nutrition</li>
        <li className={styles.toolTipContentText}>
          Aboriginal and Torres Strait Islander Health Worker assistance
        </li>
        <li className={styles.toolTipContentText}>Physiotherapy</li>
        <li className={styles.toolTipContentText}>Psychology</li>
        <li className={styles.toolTipContentText}>Exercise Physiology</li>
        <li className={styles.toolTipContentText}>Occupational therapy</li>
        <li className={styles.toolTipContentText}>
          Counselling or psychotherapy
        </li>
        <li className={styles.toolTipContentText}>Music therapy</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Personal Care</p>
      <ul id='Cat-4-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Assistance with self-care and activities of daily living
        </li>
        <li className={styles.toolTipContentText}>
          Assistance with self-administration of medications
        </li>
        <li className={styles.toolTipContentText}>
          Continence management (non-clinical)
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Nutrition</p>
      <ul id='Cat-4-list-3' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Nutrition supports</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Therapeutic Services for Independent Living
      </p>
      <ul id='Cat-4-list-4' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Acupuncture</li>
        <li className={styles.toolTipContentText}>Chiropractics</li>
        <li className={styles.toolTipContentText}>Diversional therapy</li>
        <li className={styles.toolTipContentText}>Remedial massage</li>
        <li className={styles.toolTipContentText}>Art therapy</li>
        <li className={styles.toolTipContentText}>Osteopathy</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Home or Community General Respite
      </p>
      <ul id='Cat-4-list-5' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Flexible respite</li>
        <li className={styles.toolTipContentText}>
          Community and centre-based respite
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Community Cottage Respite
      </p>
      <ul id='Cat-4-list-6' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Cottage respite</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>Care Management</p>
      <ul id='Cat-4-list-7' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Home support care management
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Restorative Care Management
      </p>
      <ul id='Cat-4-list-8' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Home support restorative care management
        </li>
      </ul>
    </div>,
    <div id='tooltip-5' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 5 - Nursing and transition care
      </h6>
      <p className={styles.toolTipContentText}>
        Services under Category 5 include:
      </p>
      <p className={styles.toolTipContentSubheading}>Nursing Care</p>
      <ul id='Cat-5-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Registered nurse clinical care
        </li>
        <li className={styles.toolTipContentText}>
          Enrolled nurse clinical care
        </li>
        <li className={styles.toolTipContentText}>
          Nursing assistant clinical care
        </li>
        <li className={styles.toolTipContentText}>Nursing care consumables</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Assistance with Transition Care
      </p>
      <ul id='Cat-5-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Transition care management
        </li>
        <li className={styles.toolTipContentText}>
          Transition care clinical services
        </li>
        <li className={styles.toolTipContentText}>
          Transition care therapy service
        </li>
        <li className={styles.toolTipContentText}>
          Transition care medication management
        </li>
        <li className={styles.toolTipContentText}>Personal affairs</li>
        <li className={styles.toolTipContentText}>
          Assistance to access medical practitioner
        </li>
        <li className={styles.toolTipContentText}>
          Transition care emergency or after hours assistance
        </li>
        <li className={styles.toolTipContentText}>
          Religious, cultural and social activities
        </li>
        <li className={styles.toolTipContentText}>Continence management</li>
        <li className={styles.toolTipContentText}>Waste disposal</li>
      </ul>
    </div>,
    <div id='tooltip-6' className={styles.toolTipContentBox}>
      <h6 className={styles.toolTipContentHeading}>
        Category 6 - Residential care (including respite)
      </h6>
      <p className={styles.toolTipContentSubheading}>
        Please note that when selecting any option under Category 6, all related
        selections will be automatically selected for you. This ensures all
        relevant requirements are displayed.
      </p>
      <p className={styles.toolTipContentText}>
        Services under Category 6 include:
      </p>
      <p className={styles.toolTipContentSubheading}>
        Residential Accommodation
      </p>
      <ul id='Cat-6-list-1' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>Accommodation</li>
        <li className={styles.toolTipContentText}>
          Accommodation administration
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Residential Clinical Care
      </p>
      <ul id='Cat-6-list-2' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Care and services plan oversight
        </li>
        <li className={styles.toolTipContentText}>
          Rehabilitation, allied health and fitness therapy programs
        </li>
        <li className={styles.toolTipContentText}>Medication management</li>
        <li className={styles.toolTipContentText}>Nursing</li>
        <li className={styles.toolTipContentText}>
          Dementia and cognition management
        </li>
        <li className={styles.toolTipContentText}>
          General access to medical services
        </li>
        <li className={styles.toolTipContentText}>
          General access to allied health services
        </li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Residential Everyday Living
      </p>
      <ul id='Cat-6-list-3' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Operational administration and emergency assistance
        </li>
        <li className={styles.toolTipContentText}>
          Telephone and internet services
        </li>
        <li className={styles.toolTipContentText}>Utilities</li>
        <li className={styles.toolTipContentText}>
          Cleaning services and waste disposal
        </li>
        <li className={styles.toolTipContentText}>Communal furnishings</li>
        <li className={styles.toolTipContentText}>
          Bedroom and bathroom furnishings
        </li>
        <li className={styles.toolTipContentText}>Toiletry goods</li>
        <li className={styles.toolTipContentText}>Personal laundry</li>
        <li className={styles.toolTipContentText}>Meals and refreshments</li>
      </ul>
      <p className={styles.toolTipContentSubheading}>
        Residential Non-clinical Care
      </p>
      <ul id='Cat-6-list-4' className={styles.toolTipList}>
        <li className={styles.toolTipContentText}>
          Care and service administration
        </li>
        <li className={styles.toolTipContentText}>Personal care assistance</li>
        <li className={styles.toolTipContentText}>Communication</li>
        <li className={styles.toolTipContentText}>Emotional support</li>
        <li className={styles.toolTipContentText}>
          Mobility and movement needs
        </li>
        <li className={styles.toolTipContentText}>Continence management</li>
        <li className={styles.toolTipContentText}>
          Recreational and social activities
        </li>
      </ul>
    </div>
  ];

  const renderColumn = (colNumber) => {
    const lower = colNumber == 0 ? 0 : 3;
    const upper = colNumber == 0 ? 3 : 6;
    const colObj = [];
    for (let i = lower; i < upper; i++) {
      colObj.push(
        <div key={`category-${i}`} className={styles.categoryControl}>
          <div className={styles.categoryHeadingContainer}>
            <label className={styles.categoryHeading}>
              <div className={styles.customCheckbox}>
                <input
                  type='checkbox'
                  name='category'
                  id={i}
                  value={categories[i].isSelected}
                  checked={categories[i].isSelected}
                  onClick={() => {
                    handleChange(i);
                  }}
                />
              </div>
              {categories[i].label}
            </label>
            <div className={styles.toolTip}>
              <Tooltip
                icon={
                  <InfoIcon
                    aria-label={`Activate tooltip for Category ${i + 1}`}
                    className={styles.toolTipIcon}
                  />
                }
                content={
                  // TODO - add actual content
                  tooltipContent[i]
                }
                isHover={false}
              />
            </div>
          </div>

          {categories[i].subCategories.map((subcategory, subIndex) => (
            <label
              key={`category-${i}-subcategory-${subIndex}`}
              className={styles.subCategory}
            >
              <div className={styles.customCheckbox}>
                <input
                  type='checkbox'
                  name='category'
                  id={`${i}-${subIndex}`}
                  value={subcategory.isSelected}
                  checked={subcategory.isSelected}
                  onClick={() => {
                    handleChange(i, subIndex);
                  }}
                />
              </div>

              {subcategory.label}
            </label>
          ))}
        </div>
      );
    }
    return (
      <div id={`Col-${colNumber}`} className={styles.columnContainer}>
        {colObj}
      </div>
    );
  };

  return (
    <div className={styles.categoryContainer}>
      {/** Had to split categories to get column styling to work */}
      {renderColumn(0)}
      {renderColumn(1)}
    </div>
  );
}
